import React, { useCallback, useEffect, useState } from 'react';
import { Database, RefreshCw } from 'lucide-react';
import { Locale, useI18n } from '../i18n';
import {
  getBackendVersionApi,
  getBackendVersionValue,
  isBackendMigrationMode,
} from '../lib/versionApi';
import BootstrapRecovery from './BootstrapRecovery';
import PortalLoader from './PortalLoader';
import { useUserStore } from '../store/userStore';

type GateState = 'checking' | 'ready' | 'migration';

const RuntimeMigrationGate: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { locale, setLocale, t } = useI18n();
  const [state, setState] = useState<GateState>('checking');
  const [apiVersion, setApiVersion] = useState('');
  const [checking, setChecking] = useState(false);
  const isInitialized = useUserStore(store => store.isInitialized);
  const initialize = useUserStore(store => store.initialize);

  const checkRuntime = useCallback(async () => {
    setChecking(true);
    try {
      const runtime = await getBackendVersionApi(true);
      setApiVersion(getBackendVersionValue(runtime));
      setState(isBackendMigrationMode(runtime) ? 'migration' : 'ready');
    } catch {
      // API connection failures are handled by the regular application UI.
      setState('ready');
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  useEffect(() => {
    void checkRuntime();
    const timer = window.setInterval(() => void checkRuntime(), 10_000);
    return () => window.clearInterval(timer);
  }, [checkRuntime]);

  if (state === 'checking' || (state === 'ready' && !isInitialized)) {
    return <PortalLoader />;
  }

  if (state !== 'migration') return <>{children}</>;

  return (
    <main className="relative min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 flex items-center justify-center px-5 py-12">
      <label className="absolute right-5 top-5">
        <span className="sr-only">Language</span>
        <select
          value={locale}
          onChange={(event) => setLocale(event.target.value as Locale)}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
        >
          <option value="nl">🇳🇱 NL</option>
          <option value="en">🇬🇧 EN</option>
        </select>
      </label>

      <section className="w-full max-w-xl rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-xl md:p-11">
        <img src="/we-dohockey-orange-black-trans.png" alt="doHockey" className="mx-auto mb-8 h-14" />
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-orange-50 text-orange-600">
          <Database size={25} aria-hidden="true" />
        </div>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-600">
          {t('migration_maintenance_eyebrow')}
        </p>
        <h1 className="text-3xl font-bold text-gray-900">{t('migration_maintenance_title')}</h1>
        <p className="mx-auto mt-4 max-w-md leading-7 text-gray-600">{t('migration_maintenance_message')}</p>
        <div className="mt-7 rounded-xl bg-gray-50 px-4 py-3 text-sm font-medium text-gray-600">
          {t('migration_maintenance_status')}{apiVersion ? ` · API ${apiVersion}` : ''}
        </div>
        <div className="mt-6 text-left"><BootstrapRecovery onlyWhenRecoveryNeeded /></div>
        <button
          type="button"
          onClick={() => void checkRuntime()}
          disabled={checking}
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-3 font-semibold text-white transition hover:bg-orange-700 disabled:opacity-60"
        >
          <RefreshCw size={17} className={checking ? 'animate-spin' : ''} aria-hidden="true" />
          {t('migration_maintenance_refresh')}
        </button>
      </section>
    </main>
  );
};

export default RuntimeMigrationGate;
